"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight, Sparkles, ShieldAlert, BellRing, MessageSquareText, LayoutGrid, CheckCircle2, type LucideIcon } from "lucide-react";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { websiteBodyText, websiteCardTitleText, websiteEyebrowText, websiteSectionTitleText } from "@/app/(website)/_components/website-type-scale";

type ScenarioVisual = "noise" | "alerts" | "brief" | "answers" | "system";

type Scenario = {
  id: string;
  title: string;
  beforeLabel: string;
  afterLabel: string;
  beforeCaption: string;
  afterCaption: string;
  accent: string;
  accentSecondary: string;
  icon: LucideIcon;
  visual: ScenarioVisual;
};

const scenarios: Scenario[] = [
  {
    id: "noise",
    title: "Noise to signal",
    beforeLabel: "Everything all at once",
    afterLabel: "One thing to act on",
    beforeCaption: "Signals buried in noise",
    afterCaption: "One clear next move",
    accent: "#a855f7",
    accentSecondary: "#ec4899",
    icon: Sparkles,
    visual: "noise",
  },
  {
    id: "alerts",
    title: "Late to early",
    beforeLabel: "Spotted after the damage",
    afterLabel: "See it while it is forming",
    beforeCaption: "Reactive alerts after the hit",
    afterCaption: "Warnings early enough to act",
    accent: "#f97316",
    accentSecondary: "#facc15",
    icon: ShieldAlert,
    visual: "alerts",
  },
  {
    id: "brief",
    title: "Scattered to aligned",
    beforeLabel: "Fragments everywhere",
    afterLabel: "A calm daily brief",
    beforeCaption: "Five tools, no shared view",
    afterCaption: "One start to the day",
    accent: "#22c55e",
    accentSecondary: "#bef264",
    icon: BellRing,
    visual: "brief",
  },
  {
    id: "answers",
    title: "Guessing to answers",
    beforeLabel: "Another dashboard dive",
    afterLabel: "Ask and understand",
    beforeCaption: "Hunting for context",
    afterCaption: "Grounded answers instantly",
    accent: "#38bdf8",
    accentSecondary: "#0ea5e9",
    icon: MessageSquareText,
    visual: "answers",
  },
  {
    id: "system",
    title: "Fragmented to connected",
    beforeLabel: "Pieces that do not connect",
    afterLabel: "The system in one view",
    beforeCaption: "Disconnected pressure points",
    afterCaption: "Everything moves together",
    accent: "#f97316",
    accentSecondary: "#fb7185",
    icon: LayoutGrid,
    visual: "system",
  },
];

function DesktopScenarioScene({ scenario, reveal }: { scenario: Scenario; reveal: boolean }) {
  const afterGlowClass = reveal ? "opacity-100 scale-100" : "opacity-70 scale-[0.96]";
  const beforeGlowClass = reveal ? "opacity-35 scale-[0.96]" : "opacity-100 scale-100";
  const beforeCardClass = reveal ? "opacity-45 scale-[0.98] blur-[0.5px]" : "opacity-100 scale-100 blur-0";
  const afterCardClass = reveal ? "opacity-100 translate-x-0 translate-y-0 scale-100" : "opacity-86 translate-x-2 translate-y-1 scale-[0.995]";
  const beforeTopClass = "top-[42%]";
  const afterTopClass = "top-[20%]";

  if (scenario.visual === "noise") {
    return (
      <div className="relative h-full overflow-hidden">
        <div className={`absolute left-[8%] top-[22%] h-40 w-40 rounded-full blur-3xl transition-all duration-700 ${beforeGlowClass}`} style={{ backgroundColor: "rgba(148,163,184,0.18)" }} />
        <div className={`absolute right-[10%] top-[24%] h-48 w-48 rounded-full blur-3xl transition-all duration-700 ${afterGlowClass}`} style={{ backgroundColor: "rgba(168,85,247,0.26)" }} />
        <div className={`absolute left-[8%] ${beforeTopClass} grid w-[34%] rotate-[-6deg] gap-3 transition-all duration-700 ${beforeCardClass}`}>
          <div className="rounded-[1.5rem] border border-white/8 bg-slate-900/80 p-4 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Unread reports</span>
              <span className="rounded-full bg-rose-500/18 px-2 py-1 text-[10px] font-bold text-rose-200">14</span>
            </div>
            <div className="mt-4 grid gap-2">
              {[
                "Campaign shift hidden in traffic",
                "Margins slipped on top category",
                "Refund spike buried in exports",
              ].map((line, index) => (
                <div key={line} className="rounded-full bg-slate-800/90 px-3 py-2 text-[11px] text-slate-300" style={{ transform: `translateX(${index % 2 === 0 ? 0 : 14}px)` }}>
                  {line}
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Ads", tone: "bg-amber-400/10 text-amber-200" },
              { label: "Store", tone: "bg-sky-400/10 text-sky-200" },
              { label: "Email", tone: "bg-fuchsia-400/10 text-fuchsia-200" },
              { label: "Support", tone: "bg-emerald-400/10 text-emerald-200" },
            ].map((item) => (
              <div key={item.label} className={`rounded-[1.2rem] border border-white/8 p-3 text-xs font-semibold ${item.tone}`}>
                {item.label}
              </div>
            ))}
          </div>
        </div>
        <div className={`absolute right-[10%] ${afterTopClass} grid w-[35%] gap-3 transition-all duration-700 ${afterCardClass}`}>
          <div className="rounded-[1.7rem] border border-white/10 bg-slate-950 p-4 shadow-[0_18px_40px_rgba(15,23,42,0.28)]">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Signal surfaced</span>
              <span className="rounded-full px-2 py-1 text-[10px] font-bold text-white" style={{ background: `linear-gradient(90deg, ${scenario.accent}, ${scenario.accentSecondary})` }}>
                Act now
              </span>
            </div>
            <p className="mt-4 text-sm font-semibold leading-snug text-white">Revenue leak found in paid traffic to a low-margin collection.</p>
            <div className="mt-4 grid gap-2">
              {[
                "Traffic up 18%",
                "Margin down 9%",
                "Action: shift budget to top converting path",
              ].map((line, index) => (
                <div key={line} className="overflow-hidden rounded-full bg-slate-900/90 px-3 py-2">
                  <div className="h-2 rounded-full" style={{ width: `${68 + index * 8}%`, background: `linear-gradient(90deg, ${scenario.accent}, ${scenario.accentSecondary})` }} />
                  <p className="mt-2 text-[11px] text-slate-300">{line}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (scenario.visual === "alerts") {
    return (
      <div className="relative h-full overflow-hidden">
        <div className={`absolute left-[10%] top-[26%] h-48 w-48 rounded-full blur-3xl transition-all duration-700 ${beforeGlowClass}`} style={{ backgroundColor: "rgba(239,68,68,0.22)" }} />
        <div className={`absolute right-[8%] top-[24%] h-52 w-52 rounded-full blur-3xl transition-all duration-700 ${afterGlowClass}`} style={{ backgroundColor: "rgba(249,115,22,0.24)" }} />
        <div className={`absolute left-[12%] ${beforeTopClass} grid w-[31%] gap-4 transition-all duration-700 ${beforeCardClass}`}>
          <div className="rounded-[1.5rem] border border-white/8 bg-slate-900/80 p-4 backdrop-blur-sm">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Caught too late</p>
            <div className="mt-4 grid gap-3">
              {[
                "Refund spike already hit profit",
                "Ad waste noticed after spend landed",
                "Inventory delay reached customers",
              ].map((line, index) => (
                <div key={line} className="flex items-center gap-3 rounded-[1.1rem] bg-slate-800/90 p-3">
                  <div className="h-3 w-3 rounded-full bg-red-400 shadow-[0_0_18px_rgba(248,113,113,0.45)]" style={{ opacity: 1 - index * 0.18 }} />
                  <p className="text-[11px] text-slate-300">{line}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className={`absolute right-[10%] ${afterTopClass} grid w-[33%] gap-4 transition-all duration-700 ${afterCardClass}`}>
          <div className="rounded-[1.6rem] border border-white/10 bg-slate-950 p-4 shadow-[0_12px_34px_rgba(15,23,42,0.24)]">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Early warning</span>
              <span className="rounded-full px-2 py-1 text-[10px] font-bold text-slate-950" style={{ background: `linear-gradient(90deg, ${scenario.accent}, ${scenario.accentSecondary})` }}>
                36 hrs lead
              </span>
            </div>
            <div className="mt-4 grid gap-3">
              {[76, 62, 48].map((value, index) => (
                <div key={`alert-after-${index}`} className="rounded-[1.2rem] bg-slate-900/90 p-3">
                  <div className="flex items-center justify-between text-[11px] text-slate-300">
                    <span>{["Returns risk", "CAC drift", "Fulfillment lag"][index]}</span>
                    <span>{value}%</span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-slate-800/90">
                    <div className="h-2 rounded-full" style={{ width: `${value}%`, background: `linear-gradient(90deg, ${scenario.accent}, ${scenario.accentSecondary})` }} />
                  </div>
                </div>
              ))}
              <div className="rounded-[1.1rem] border border-emerald-400/20 bg-emerald-400/10 px-3 py-2 text-[11px] font-medium text-emerald-100">
                Recommended: review the refund path before the spike compounds.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (scenario.visual === "brief") {
    return (
      <div className="relative h-full overflow-hidden">
        <div className={`absolute left-[8%] top-[22%] h-44 w-44 rounded-full blur-3xl transition-all duration-700 ${beforeGlowClass}`} style={{ backgroundColor: "rgba(148,163,184,0.18)" }} />
        <div className={`absolute right-[8%] top-[24%] h-48 w-48 rounded-full blur-3xl transition-all duration-700 ${afterGlowClass}`} style={{ backgroundColor: "rgba(34,197,94,0.24)" }} />
        <div className={`absolute left-[12%] ${beforeTopClass} grid w-[35%] grid-cols-2 gap-3 rotate-[-4deg] transition-all duration-700 ${beforeCardClass}`}>
          {[
            { label: "Ads", sub: "3 updates" },
            { label: "Analytics", sub: "2 exports" },
            { label: "Email", sub: "1 campaign" },
            { label: "Store", sub: "4 shifts" },
            { label: "Support", sub: "6 tickets" },
            { label: "Inventory", sub: "2 flags" },
          ].map((item, index) => (
            <div key={item.label} className="rounded-[1.4rem] border border-white/8 bg-slate-900/78 p-4 backdrop-blur-sm" style={{ opacity: 1 - index * 0.06 }}>
              <p className="text-xs font-semibold text-white">{item.label}</p>
              <p className="mt-2 text-[11px] text-slate-400">{item.sub}</p>
            </div>
          ))}
        </div>
        <div className={`absolute right-[12%] ${afterTopClass} h-[46%] w-[31%] rounded-[2rem] border border-white/10 bg-slate-950 p-4 shadow-[0_14px_40px_rgba(15,23,42,0.26)] transition-all duration-700 ${afterCardClass}`}>
          <div className="rounded-full bg-slate-900/90 px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-slate-400">Daily brief</div>
          <div className="mt-4 space-y-3">
            <div className="rounded-[1.1rem] bg-slate-900/90 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-200">Today first</p>
              <p className="mt-2 text-sm font-medium text-white">Margin slipped on paid traffic.</p>
            </div>
            <div className="grid gap-2">
              {[
                "Top category conversion +12%",
                "Refund risk in one SKU cluster",
                "Inventory healthy for best sellers",
              ].map((line, index) => (
                <div key={line} className="overflow-hidden rounded-full bg-slate-900/90 px-3 py-2">
                  <div className="h-2 rounded-full" style={{ width: `${72 - index * 10}%`, background: `linear-gradient(90deg, ${scenario.accent}, ${scenario.accentSecondary})` }} />
                  <p className="mt-2 text-[11px] text-slate-300">{line}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (scenario.visual === "answers") {
    return (
      <div className="relative h-full overflow-hidden">
        <div className={`absolute left-[10%] top-[24%] h-44 w-44 rounded-full blur-3xl transition-all duration-700 ${beforeGlowClass}`} style={{ backgroundColor: "rgba(148,163,184,0.18)" }} />
        <div className={`absolute right-[8%] top-[24%] h-48 w-48 rounded-full blur-3xl transition-all duration-700 ${afterGlowClass}`} style={{ backgroundColor: "rgba(56,189,248,0.24)" }} />
        <div className={`absolute left-[12%] ${beforeTopClass} grid w-[32%] gap-3 rotate-[-5deg] transition-all duration-700 ${beforeCardClass}`}>
          <div className="rounded-[1.4rem] border border-white/8 bg-slate-900/78 p-4 backdrop-blur-sm">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Dashboard dive</p>
            <div className="mt-4 space-y-3">
              {[
                "Open revenue report",
                "Filter date range",
                "Cross-check refunds",
                "Still missing context",
              ].map((line, index) => (
                <div key={line} className="rounded-full bg-slate-800/90 px-3 py-2 text-[11px] text-slate-300" style={{ opacity: 1 - index * 0.1 }}>
                  {line}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className={`absolute right-[10%] ${afterTopClass} flex w-[35%] flex-col gap-4 transition-all duration-700 ${afterCardClass}`}>
          <div className="rounded-[1.5rem] rounded-br-md px-5 py-4 text-white shadow-[0_20px_60px_rgba(14,116,144,0.35)]" style={{ background: `linear-gradient(135deg, ${scenario.accent}, ${scenario.accentSecondary})` }}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/70">Question</p>
            <p className="mt-2 text-sm font-medium leading-relaxed">What changed in revenue this week?</p>
          </div>
          <div className="ml-8 rounded-[1.5rem] rounded-bl-md border border-white/10 bg-slate-950 px-5 py-5 shadow-[0_14px_40px_rgba(15,23,42,0.26)]">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Answer</p>
            <p className="mt-2 text-sm leading-relaxed text-white">Paid search lifted top-line revenue, but returns in one category erased most of the gain.</p>
            <div className="mt-4 inline-flex rounded-full bg-cyan-400/12 px-3 py-1.5 text-[11px] font-medium text-cyan-100">
              Recommended next step: review the refund path.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full overflow-hidden">
      <div className={`absolute left-[10%] top-[22%] h-44 w-44 rounded-full blur-3xl transition-all duration-700 ${beforeGlowClass}`} style={{ backgroundColor: "rgba(148,163,184,0.18)" }} />
      <div className={`absolute right-[10%] top-[24%] h-52 w-52 rounded-full blur-3xl transition-all duration-700 ${afterGlowClass}`} style={{ backgroundColor: "rgba(249,115,22,0.24)" }} />
      <div className={`absolute left-[10%] ${beforeTopClass} w-[36%] rotate-[-5deg] transition-all duration-700 ${beforeCardClass}`}>
        <div className="relative h-56">
          {[
            {
              label: "Ads manager",
              note: "Spend up, no margin context",
              className: "left-[4%] top-[0%]",
            },
            {
              label: "Store analytics",
              note: "Conversion shift, no campaign link",
              className: "right-[2%] top-[10%]",
            },
            {
              label: "Support inbox",
              note: "Refund complaints rising",
              className: "left-[10%] bottom-[14%]",
            },
            {
              label: "Inventory sheet",
              note: "Low stock hidden from demand",
              className: "right-[8%] bottom-[0%]",
            },
          ].map((card, index) => (
            <div key={card.label} className={`absolute ${card.className} w-[46%] rounded-[1.3rem] border border-white/8 bg-slate-900/80 p-3 backdrop-blur-sm`} style={{ opacity: 1 - index * 0.06 }}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-semibold text-white">{card.label}</p>
                <span className="rounded-full bg-rose-500/14 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-rose-200">isolated</span>
              </div>
              <p className="mt-2 text-[10px] leading-relaxed text-slate-400">{card.note}</p>
              <div className="mt-3 h-1.5 w-14 rounded-full bg-slate-700" />
            </div>
          ))}
          <div className="absolute left-[38%] top-[34%] h-px w-[18%] rotate-[8deg] border-t border-dashed border-white/10" />
          <div className="absolute left-[30%] top-[50%] h-px w-[16%] rotate-[-22deg] border-t border-dashed border-white/10" />
          <div className="absolute right-[28%] top-[56%] h-px w-[16%] rotate-[24deg] border-t border-dashed border-white/10" />
          <div className="absolute right-[34%] top-[34%] h-px w-[16%] rotate-[-10deg] border-t border-dashed border-white/10" />
        </div>
      </div>
      <div className={`absolute right-[10%] ${afterTopClass} w-[36%] transition-all duration-700 ${afterCardClass}`}>
        <div className="rounded-[1.9rem] border border-white/10 bg-slate-950 p-4 shadow-[0_14px_40px_rgba(15,23,42,0.26)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Connected system</p>
              <p className="mt-1 text-sm font-semibold text-white">One picture across every channel</p>
            </div>
            <div className="rounded-full px-2.5 py-1 text-[10px] font-bold text-slate-950" style={{ background: `linear-gradient(90deg, ${scenario.accent}, ${scenario.accentSecondary})` }}>
              Synced live
            </div>
          </div>
          <div className="relative mt-4 h-40 rounded-[1.5rem] bg-slate-900/90 p-4">
            <div className="absolute left-1/2 top-1/2 z-10 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-center text-[10px] font-bold uppercase tracking-[0.16em] text-slate-950" style={{ background: `linear-gradient(135deg, ${scenario.accent}, ${scenario.accentSecondary})` }}>
              Storita
            </div>
            {[
              { label: "Ads", className: "left-[4%] top-[14%]" },
              { label: "Store", className: "right-[4%] top-[14%]" },
              { label: "Support", className: "left-[2%] bottom-[12%]" },
              { label: "Inventory", className: "right-[2%] bottom-[12%]" },
            ].map((node) => (
              <div key={node.label} className={`absolute ${node.className} z-10 rounded-[1rem] border border-white/10 bg-slate-950/95 px-3 py-2`}>
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white">{node.label}</p>
                <div className="mt-2 h-1.5 w-10 rounded-full" style={{ background: `linear-gradient(90deg, ${scenario.accent}, ${scenario.accentSecondary})` }} />
              </div>
            ))}
            <div className="absolute left-[24%] top-[34%] h-px w-[22%] bg-white/18" />
            <div className="absolute right-[24%] top-[34%] h-px w-[22%] bg-white/18" />
            <div className="absolute left-[24%] bottom-[34%] h-px w-[22%] bg-white/18" />
            <div className="absolute right-[24%] bottom-[34%] h-px w-[22%] bg-white/18" />
            <div className="absolute left-[47%] top-[18%] h-[22%] w-px bg-white/14" />
            <div className="absolute left-[47%] bottom-[18%] h-[22%] w-px bg-white/14" />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            {[
              "Spend tied to margin",
              "Support tied to refunds",
              "Stock tied to demand",
            ].map((line) => (
              <div key={line} className="rounded-[1rem] border border-white/8 bg-slate-900/85 px-3 py-2 text-[10px] leading-relaxed text-slate-300">
                {line}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function BeforeAfterGallery() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isRevealed, setIsRevealed] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const dividerClass = isRevealed ? "translate-x-[100%] opacity-100" : "translate-x-0 opacity-82";

  useEffect(() => {
    if (hasInteracted) {
      return;
    }

    const revealTimer = window.setTimeout(() => setIsRevealed(true), 1200);
    const advanceTimer = window.setInterval(() => {
      setActiveIndex((previous) => (previous + 1) % scenarios.length);
      setIsRevealed(false);
      window.setTimeout(() => setIsRevealed(true), 320);
    }, 5200);

    return () => {
      window.clearTimeout(revealTimer);
      window.clearInterval(advanceTimer);
    };
  }, [hasInteracted]);

  const activeScenario = scenarios[activeIndex];
  const Icon = activeScenario.icon;

  const activateScenario = (index: number) => {
    setHasInteracted(true);
    setActiveIndex(index);
    setIsRevealed(true);
  };

  const revealActiveScenario = () => {
    setHasInteracted(true);
    setIsRevealed(true);
  };

  const cycleScenario = (direction: number) => {
    setHasInteracted(true);
    setActiveIndex((previous) => (previous + direction + scenarios.length) % scenarios.length);
    setIsRevealed(true);
  };

  return (
    <section className="overflow-hidden py-16 md:py-22">
      <div className="container mx-auto px-4 md:px-6">
        <ScrollReveal>
          <div className="mx-auto mb-8 max-w-3xl text-center md:mb-12">
            <p className={`text-slate-500 ${websiteEyebrowText}`}>Before and after Storita</p>
            <h2 className={`mt-3 text-white ${websiteSectionTitleText}`}>
              The same signals, <span className="bg-gradient-to-r from-rose-300 via-white to-emerald-300 bg-clip-text text-transparent">completely different headline</span>.
            </h2>
            <p className={`mx-auto mt-4 max-w-2xl text-slate-300 ${websiteBodyText}`}>
              See how the story changes when weak signals become visible early, organized, and ready to act on.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={100}>
          <div className="relative">
            <div className="pointer-events-none absolute inset-x-[12%] top-[6%] h-40 rounded-full blur-3xl transition-all duration-700" style={{ backgroundColor: `${activeScenario.accent}18` }} />
            <div className="pointer-events-none absolute bottom-[10%] right-[8%] h-52 w-52 rounded-full blur-3xl transition-all duration-700" style={{ backgroundColor: `${activeScenario.accentSecondary}18` }} />

            <div className="hidden md:block">
              <div className="relative h-[33rem] overflow-hidden" onMouseEnter={revealActiveScenario} onFocusCapture={revealActiveScenario}>
                <div className="absolute inset-y-0 left-0 w-1/2 bg-[linear-gradient(180deg,rgba(15,23,42,0.92),rgba(2,6,23,0.78))]" />
                <div className="absolute inset-y-0 right-0 w-1/2 bg-[linear-gradient(180deg,rgba(5,46,22,0.1),rgba(2,6,23,0.24))]" />
                <div className="absolute inset-y-0 right-0 w-1/2 bg-[radial-gradient(circle_at_65%_35%,rgba(34,197,94,0.12),transparent_28%)] transition-all duration-700" />
                <DesktopScenarioScene scenario={activeScenario} reveal={isRevealed} />
                <div className={`absolute inset-y-0 left-0 w-1/2 bg-[linear-gradient(180deg,rgba(2,6,23,0.08),rgba(2,6,23,0.03))] transition-all duration-700 ${dividerClass}`} />

                <div className="absolute left-0 top-0 z-20 w-[46%] max-w-[30rem]">
                  <div
                    className="relative w-full bg-[linear-gradient(180deg,rgba(148,163,184,0.26),rgba(71,85,105,0.38))] px-6 pb-4 pt-8 shadow-[0_18px_40px_rgba(148,163,184,0.12)] backdrop-brightness-110"
                    style={{ clipPath: "polygon(0 0, 100% 0, 90% 100%, 0 100%)" }}
                  >
                    <h3 className={`${websiteCardTitleText} text-white`}>{activeScenario.beforeLabel}</h3>
                    <p className={`mt-3 max-w-sm text-slate-300 ${websiteBodyText}`}>{activeScenario.beforeCaption}</p>
                  </div>
                  <div className="mt-3 inline-flex bg-rose-500 px-4 py-2 text-[11px] font-black uppercase tracking-[0.28em] text-white shadow-[0_14px_30px_rgba(244,63,94,0.28)]">
                    Before
                  </div>
                </div>

                <div className="absolute bottom-8 right-0 z-20 w-[48%] max-w-[31rem] text-right">
                  <div className="inline-flex items-center gap-3 bg-emerald-400 px-4 py-2 text-[11px] font-black uppercase tracking-[0.28em] text-slate-950 shadow-[0_14px_30px_rgba(34,197,94,0.28)]">
                    <Image src="/logo.png" alt="Storita" width={22} height={22} className="h-[22px] w-[22px] object-contain" />
                    <span>After</span>
                  </div>
                  <div className="pointer-events-none absolute right-2 -top-24 flex items-center justify-center">
                    <div className="absolute h-28 w-28 rounded-full bg-emerald-300/22 blur-2xl animate-[storita-win-pulse_2.6s_ease-in-out_infinite]" />
                    <div className="relative flex h-24 w-24 items-center justify-center rounded-full border border-emerald-200/35 bg-emerald-400/18 shadow-[0_18px_40px_rgba(34,197,94,0.28)] backdrop-blur-sm animate-[storita-win-float_3s_ease-in-out_infinite]">
                      <CheckCircle2 className="h-14 w-14 text-emerald-200 drop-shadow-[0_0_18px_rgba(187,247,208,0.75)] animate-[storita-win-pop_2.2s_ease-in-out_infinite]" strokeWidth={2.6} />
                    </div>
                  </div>
                  <div
                    className="mt-3 ml-auto w-full bg-[linear-gradient(180deg,rgba(226,232,240,0.14),rgba(71,85,105,0.32))] px-6 py-4 shadow-[0_18px_40px_rgba(148,163,184,0.14)] backdrop-brightness-110"
                    style={{ clipPath: "polygon(10% 0, 100% 0, 100% 100%, 0 100%)" }}
                  >
                    <h3 className={`${websiteCardTitleText} text-white`}>{activeScenario.afterLabel}</h3>
                    <p className={`mt-3 ml-auto max-w-sm text-slate-100 ${websiteBodyText}`}>{activeScenario.afterCaption}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:hidden">
              <div className="mb-5 text-center">
                <div className="inline-flex items-center gap-2 rounded-full bg-slate-950/78 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-200 backdrop-blur-sm">
                  <Icon className="h-3.5 w-3.5" style={{ color: activeScenario.accent }} />
                  <span>{activeScenario.title}</span>
                </div>
              </div>

              <div className="relative px-10">
                <button
                  type="button"
                  aria-label="Previous conclusion"
                  onClick={() => cycleScenario(-1)}
                  className="absolute left-0 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-slate-950/78 text-slate-200 shadow-[0_12px_24px_rgba(2,6,23,0.22)] transition-colors hover:bg-slate-900"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>

                <div className="rounded-[1.8rem] bg-[linear-gradient(180deg,rgba(226,232,240,0.08),rgba(30,41,59,0.42))] px-5 py-6 text-center shadow-[0_18px_50px_rgba(2,6,23,0.22)]">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-400">Conclusion</p>
                  <div className="mx-auto mb-4 flex w-fit items-center gap-3 bg-emerald-400 px-4 py-2 text-[10px] font-black uppercase tracking-[0.24em] text-slate-950 shadow-[0_14px_30px_rgba(34,197,94,0.28)]">
                    <Image src="/logo.png" alt="Storita" width={20} height={20} className="h-5 w-5 object-contain" />
                    <span>After</span>
                  </div>
                  <div className="pointer-events-none relative mx-auto mb-4 flex h-24 w-24 items-center justify-center">
                    <div className="absolute h-24 w-24 rounded-full bg-emerald-300/24 blur-2xl animate-[storita-win-pulse_2.6s_ease-in-out_infinite]" />
                    <div className="relative flex h-20 w-20 items-center justify-center rounded-full border border-emerald-200/35 bg-emerald-400/18 shadow-[0_18px_40px_rgba(34,197,94,0.28)] backdrop-blur-sm animate-[storita-win-float_3s_ease-in-out_infinite]">
                      <CheckCircle2 className="h-12 w-12 text-emerald-200 drop-shadow-[0_0_18px_rgba(187,247,208,0.75)] animate-[storita-win-pop_2.2s_ease-in-out_infinite]" strokeWidth={2.6} />
                    </div>
                  </div>
                  <h3 className={`${websiteCardTitleText} text-white`}>{activeScenario.afterLabel}</h3>
                  <p className={`mt-2 text-slate-300 ${websiteBodyText}`}>{activeScenario.afterCaption}</p>
                  <p className={`mt-4 text-slate-400 ${websiteBodyText}`}>Storita turns scattered signals into a clear next move without making mobile users decode a miniature desktop demo.</p>
                </div>

                <button
                  type="button"
                  aria-label="Next conclusion"
                  onClick={() => cycleScenario(1)}
                  className="absolute right-0 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-slate-950/78 text-slate-200 shadow-[0_12px_24px_rgba(2,6,23,0.22)] transition-colors hover:bg-slate-900"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="mt-4 hidden flex-wrap items-center justify-center gap-2.5 md:flex">
              {scenarios.map((scenario, index) => {
                const ScenarioIcon = scenario.icon;
                const isActive = index === activeIndex;

                return (
                  <button
                    key={scenario.id}
                    type="button"
                    onClick={() => activateScenario(index)}
                    className={`inline-flex items-center gap-2.5 rounded-full px-3.5 py-2 text-left transition-all duration-500 ${isActive ? "bg-slate-900 text-white shadow-[0_12px_32px_rgba(2,6,23,0.34)]" : "bg-slate-950/70 text-slate-400 hover:bg-slate-900/90 hover:text-slate-200"}`}
                  >
                    <span className="flex h-7 w-7 items-center justify-center rounded-full" style={{ backgroundColor: isActive ? `${scenario.accent}22` : "rgba(15,23,42,0.9)" }}>
                      <ScenarioIcon className="h-4 w-4" style={{ color: scenario.accent }} />
                    </span>
                    <span className="text-sm font-medium">{scenario.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </ScrollReveal>

      </div>

      <style jsx>{`
        @keyframes storita-win-float {
          0%,
          100% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(-6px) scale(1.04);
          }
        }

        @keyframes storita-win-pop {
          0%,
          100% {
            transform: scale(1) rotate(0deg);
          }
          20% {
            transform: scale(1.14) rotate(-4deg);
          }
          42% {
            transform: scale(0.96) rotate(3deg);
          }
          62% {
            transform: scale(1.08) rotate(-2deg);
          }
        }

        @keyframes storita-win-pulse {
          0%,
          100% {
            opacity: 0.45;
            transform: scale(0.9);
          }
          50% {
            opacity: 0.9;
            transform: scale(1.18);
          }
        }
      `}</style>
    </section>
  );
}